import { Pencil, Tag } from 'lucide-react';
import { useMutation } from '@platform/api-client';
import { useAuth } from '@platform/auth-client';
import { Button, EmptyState, Skeleton } from '@platform/design-system';
import type { ShippingClient } from '@platform/openapi';
import { useState } from 'react';
import { LoadFailed } from '../LoadFailed.tsx';
import { usePageContext } from '../usePageContext.ts';
import { CARRIERS_MANAGE_ROLE } from './carriers.ts';
import { useSenderProfile, type SenderBody } from './sender.ts';
import { SenderSheet } from './SenderSheet.tsx';
import { SETTINGS_SECTIONS } from './settings-sections.ts';
import { SettingsSection } from './SettingsSection.tsx';
import styles from './Settings.module.css';

export type SenderSectionProps = { shipping: ShippingClient };

export const SenderSection = ({ shipping }: SenderSectionProps) => {
  const { t, notify, invalidate, describeError } = usePageContext();
  const canManage = useAuth().hasRole(CARRIERS_MANAGE_ROLE);
  const sender = useSenderProfile(shipping);
  const [editing, setEditing] = useState(false);
  const save = useMutation({
    mutationFn: (body: SenderBody) => shipping.put('/admin/shipping/sender', { body }),
    onSuccess: async () => {
      notify(t('sender.saved'));
      setEditing(false);
      await invalidate('sender');
    },
  });
  const profile = sender.data?.profile ?? null;
  const edit = canManage ? (
    <Button
      icon={<Pencil size={20} aria-hidden="true" />}
      variant="outline"
      onClick={() => {
        save.reset();
        setEditing(true);
      }}
    >
      {profile === null ? t('sender.fill') : t('sender.edit')}
    </Button>
  ) : undefined;

  const body = () => {
    if (sender.isPending) return <Skeleton height="96px" label={t('sender.title')} />;
    if (sender.isError)
      return (
        <LoadFailed
          description={describeError(sender.error)}
          onRetry={() => {
            void sender.refetch();
          }}
        />
      );
    if (profile === null)
      return <EmptyState title={t('sender.none')} description={t('sender.noneHelp')} action={edit} />;
    return (
      <figure className={styles['label']} aria-label={t('sender.previewLabel')}>
        <figcaption className={styles['labelCaption']}>{t('sender.previewCaption')}</figcaption>
        <strong className={styles['labelName']}>{profile.name}</strong>
        <bdi dir="ltr" className={styles['labelLine']}>
          {profile.phone}
        </bdi>
        <span className={styles['labelLine']}>{profile.address}</span>
        <span className={profile.taxId === null ? styles['labelMissing'] : styles['labelLine']}>
          {profile.taxId === null
            ? t('sender.taxIdMissing')
            : t('sender.taxIdLine', { value: profile.taxId })}
        </span>
      </figure>
    );
  };

  return (
    <SettingsSection
      id={SETTINGS_SECTIONS.labels}
      icon={Tag}
      title={t('sender.sectionTitle')}
      description={t('sender.sectionContext')}
      action={profile === null ? undefined : edit}
    >
      {body()}
      {editing ? (
        <SenderSheet
          profile={profile}
          submitting={save.isPending}
          errorMessage={save.isError ? (describeError(save.error) ?? null) : null}
          onClose={() => {
            setEditing(false);
          }}
          onSubmit={(values) => {
            save.mutate(values);
          }}
        />
      ) : null}
    </SettingsSection>
  );
};
